import React from 'react';
import './ConfigStatusView2Skeleton.css';

function SkelBar({ className = '' }) {
  return <span className={`pcs2-skel-bar ${className}`.trim()} aria-hidden />;
}

function SkelListRows({ count = 7 }) {
  return (
    <div className="pcs2-skel-list">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="pcs2-skel-list-row">
          <SkelBar className="pcs2-skel-bar--icon" />
          <SkelBar className="pcs2-skel-bar--label" />
          <SkelBar className="pcs2-skel-bar--pin" />
        </div>
      ))}
    </div>
  );
}

function SkelSettingRows({ count = 2 }) {
  return (
    <div className="pcs2-skel-settings">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="pcs2-skel-setting-row">
          <div className="pcs2-skel-setting-copy">
            <SkelBar className="pcs2-skel-bar--title" />
            <SkelBar className="pcs2-skel-bar--desc" />
          </div>
          <SkelBar className="pcs2-skel-bar--switch" />
        </div>
      ))}
    </div>
  );
}

function SkelSection({ children }) {
  return (
    <section className="pcs2-section pcs2-skel-section">
      <div className="pcs2-section-aside">
        <div className="pcs2-skel-aside">
          <SkelBar className="pcs2-skel-bar--aside-icon" />
          <div className="pcs2-skel-aside-text">
            <SkelBar className="pcs2-skel-bar--aside-title" />
            <SkelBar className="pcs2-skel-bar--aside-desc" />
            <SkelBar className="pcs2-skel-bar--aside-desc pcs2-skel-bar--short" />
          </div>
        </div>
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function SkelTableBody({ rows = 8 }) {
  return (
    <div className="pcs2-skel-table">
      <div className="pcs2-skel-table-toolbar">
        <SkelBar className="pcs2-skel-bar--search" />
        <SkelBar className="pcs2-skel-bar--chip" />
        <SkelBar className="pcs2-skel-bar--chip" />
      </div>
      <div className="pcs2-skel-table-head">
        <SkelBar className="pcs2-skel-bar--th" />
        <SkelBar className="pcs2-skel-bar--th pcs2-skel-bar--th-mid" />
        <SkelBar className="pcs2-skel-bar--th pcs2-skel-bar--th-sm" />
        <SkelBar className="pcs2-skel-bar--th pcs2-skel-bar--th-sm" />
      </div>
      <div className="pcs2-skel-table-body">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="pcs2-skel-table-row">
            <SkelBar className="pcs2-skel-bar--td" />
            <SkelBar className="pcs2-skel-bar--td pcs2-skel-bar--td-mid" />
            <SkelBar className="pcs2-skel-bar--td pcs2-skel-bar--td-sm" />
            <SkelBar className="pcs2-skel-bar--td pcs2-skel-bar--td-sm" />
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusSkeleton() {
  return (
    <>
      <SkelSection>
        <div className="pcs2-skel-subblock">
          <SkelBar className="pcs2-skel-bar--subhead" />
          <SkelBar className="pcs2-skel-bar--subdesc" />
          <div className="pcs2-skel-card">
            <SkelListRows count={7} />
          </div>
        </div>
        <div className="pcs2-skel-card pcs2-skel-card--flat">
          <SkelSettingRows count={2} />
        </div>
        <div className="pcs2-skel-card pcs2-skel-card--flat pcs2-skel-card--spaced">
          <SkelSettingRows count={2} />
        </div>
      </SkelSection>
      <hr className="pcs2-divider" />
      <SkelSection>
        <div className="pcs2-skel-subblock">
          <SkelBar className="pcs2-skel-bar--subhead" />
          <SkelBar className="pcs2-skel-bar--subdesc" />
          <div className="pcs2-skel-card pcs2-skel-card--flat">
            <div className="pcs2-skel-pills">
              {Array.from({ length: 6 }, (_, i) => (
                <SkelBar key={i} className="pcs2-skel-bar--pill" />
              ))}
            </div>
          </div>
        </div>
      </SkelSection>
      <hr className="pcs2-divider" />
      <SkelSection>
        <div className="pcs2-skel-subblock">
          <SkelBar className="pcs2-skel-bar--subhead" />
          <SkelBar className="pcs2-skel-bar--subdesc" />
          <div className="pcs2-skel-card">
            <SkelListRows count={7} />
          </div>
        </div>
      </SkelSection>
      <div className="pcs2-skel-fab" aria-hidden>
        <SkelBar className="pcs2-skel-bar--fab" />
      </div>
    </>
  );
}

function SettingsSkeleton({ rows = 2, listRows = 0, embedded = false }) {
  const body = (
    <div className="pcs2-skel-subblock">
      {listRows > 0 ? (
        <>
          {!embedded ? (
            <>
              <SkelBar className="pcs2-skel-bar--subhead" />
              <SkelBar className="pcs2-skel-bar--subdesc" />
            </>
          ) : null}
          <div className="pcs2-skel-card">
            <SkelListRows count={listRows} />
          </div>
        </>
      ) : null}
      <div className={`pcs2-skel-card pcs2-skel-card--flat${listRows > 0 ? ' pcs2-skel-card--spaced' : ''}`}>
        <SkelSettingRows count={rows} />
      </div>
    </div>
  );
  if (embedded) return body;
  return <SkelSection>{body}</SkelSection>;
}

function ListBodySkeleton({ count = 6 }) {
  return (
    <div className="pcs2-skel-card">
      <SkelListRows count={count} />
    </div>
  );
}

function TableSkeleton({ embedded = false, rows = 8 }) {
  const body = (
    <div className="pcs2-skel-subblock">
      {!embedded ? (
        <>
          <SkelBar className="pcs2-skel-bar--subhead" />
          <SkelBar className="pcs2-skel-bar--subdesc" />
        </>
      ) : null}
      <div className="pcs2-skel-card pcs2-skel-card--table">
        <SkelTableBody rows={rows} />
      </div>
    </div>
  );
  if (embedded) return body;
  return <SkelSection>{body}</SkelSection>;
}

function EmailSkeleton() {
  return (
    <>
      <SkelSection>
        <div className="pcs2-skel-subblock">
          <SkelBar className="pcs2-skel-bar--subhead" />
          <SkelBar className="pcs2-skel-bar--subdesc" />
          <div className="pcs2-skel-card pcs2-skel-card--flat">
            <SkelSettingRows count={1} />
          </div>
        </div>
      </SkelSection>
      <hr className="pcs2-divider" />
      <SkelSection>
        <div className="pcs2-skel-subblock">
          <SkelBar className="pcs2-skel-bar--subhead" />
          <SkelBar className="pcs2-skel-bar--subdesc" />
          <div className="pcs2-skel-card">
            <SkelListRows count={5} />
          </div>
        </div>
      </SkelSection>
    </>
  );
}

export const PROCESS_SKELETON_BY_SUBMENU = {
  'config-status-2': 'status',
  'coluna-em-aberto': 'settings',
  oficina: 'oficina',
  'condicoes-intervencao': 'table',
  'defeitos-verve': 'table',
  'cadastros-marcas-subgrupos': 'table',
  'notificacoes-email': 'email',
};

/**
 * Skeleton das telas de Processos / Go.
 * @param {'status'|'settings'|'oficina'|'table'|'tableBody'|'listBody'|'email'} variant
 */
export default function ProcessConfigSkeleton({
  variant = 'status',
  ariaLabel = 'Carregando configuração',
  embedded = false,
}) {
  const embed = embedded || variant === 'tableBody' || variant === 'listBody';
  let content = null;
  if (variant === 'settings') content = <SettingsSkeleton rows={2} embedded={embed} />;
  else if (variant === 'oficina') content = <SettingsSkeleton rows={1} listRows={1} embedded={embed} />;
  else if (variant === 'table') content = <TableSkeleton embedded={embedded} rows={8} />;
  else if (variant === 'tableBody') content = <TableSkeleton embedded rows={8} />;
  else if (variant === 'listBody') content = <ListBodySkeleton count={6} />;
  else if (variant === 'email') content = <EmailSkeleton />;
  else content = <StatusSkeleton />;

  if (embed) {
    return (
      <div className="pcs2-skel-page pcs2-skel-page--embedded" aria-busy="true" aria-label={ariaLabel}>
        {content}
      </div>
    );
  }

  return (
    <div className="pcs2-page pcs2-skel-page" aria-busy="true" aria-label={ariaLabel}>
      {content}
    </div>
  );
}

/** Alias da tela principal (status). */
export function ConfigStatusView2Skeleton() {
  return <ProcessConfigSkeleton variant="status" ariaLabel="Carregando configuração de processos" />;
}
